
import { Component, Mixins } from 'vue-property-decorator';
import moment from 'moment';
import { isTaskCompleted, isTaskFailed } from '@/helpers';
import OperationComponentMixin from '@/mixins/OperationComponentMixin.vue';

moment.locale('it');

@Component
export default class OperationConnection extends Mixins(OperationComponentMixin) {
  get connectionDate(): string {
    return this.operation
      && this.operation.connectionDate
      && moment(this.operation.connectionDate).isValid()
      ? moment(this.operation.connectionDate).format('L')
      : '';
  }

  get connectionActiveDate(): string {
    return this.operation
      && this.operation.connectionActiveDate
      && moment(this.operation.connectionActiveDate).isValid()
      ? moment(this.operation.connectionActiveDate).format('L')
      : '';
  }

  get suggestedEgridConnectionDate1(): string {
    return this.operation
      && this.operation.suggestedEgridConnectionDate1
      && moment(this.operation.suggestedEgridConnectionDate1).isValid()
      ? moment(this.operation.suggestedEgridConnectionDate1).format('L')
      : '';
  }

  get suggestedEgridConnectionDate2(): string {
    return this.operation
      && this.operation.suggestedEgridConnectionDate2
      && moment(this.operation.suggestedEgridConnectionDate2).isValid()
      ? moment(this.operation.suggestedEgridConnectionDate2).format('L')
      : '';
  }

  get suggestedEgridConnectionTime1(): string | null {
    return this.operationChild?.suggestedEgridConnectionTime1 || null;
  }

  get suggestedEgridConnectionTime2(): string | null {
    return this.operationChild?.suggestedEgridConnectionTime2 || null;
  }

  get egridConnectionTime(): string | null {
    return this.operationChild?.egridConnectionTime || null;
  }

  get egridConnectionNotes(): string | null {
    return this.operationChild?.egridConnectionNotes || null;
  }

  get egridConnectionStatus(): string | null {
    return this.operationChild?.egridConnectionStatus ?? null;
  }

  get connectionLastStep(): string | null {
    return this.operation?.connectionLastStep ?? null;
  }

  get connectionLastStepDate(): string | null {
    return this.operation
      && this.operation.connectionLastStepDate
      && moment(this.operation.connectionLastStepDate).isValid()
      ? moment(this.operation.connectionLastStepDate).format('L')
      : null;
  }

  // Allacciamento (seconda colonna)

  get connectionColumnClass(): string {
    // Se il task è completato o in errore, colora ogni colonna rispettivamente di verde o rosso
    if (isTaskCompleted(this.operationChild, 'R')) return 'green';
    if (isTaskFailed(this.operationChild, 'R')) return 'red';

    // Se il task è in lavorazione e l'allaccio è confermato, la colonna è verde.
    if (this.egridConnectionStatus === 'connected') return 'green';

    // Se la data è concordata...
    if (this.egridConnectionStatus === 'not-confirmed'
      || this.egridConnectionStatus === 'no-confirmation-required') return 'yellow-white';

    // Se la data è impostata e confermata...
    if (this.egridConnectionStatus === 'confirmed'
      && this.operationChild !== null
      && this.operationChild.egridConnectionDate !== null) {
      // ... Mostra la colonna gialla se la data è precedente alla data odierna,
      // viceversa arancione.
      const todayDate = moment().startOf('day').toDate();
      const connectionDate = moment(this.operationChild.egridConnectionDate).startOf('day').toDate();

      return moment(todayDate).isSameOrBefore(connectionDate) ? 'yellow' : 'orange';
    }

    // Se l'allaccio è non applicabile
    if (this.egridConnectionStatus === 'na') return 'grey lighten-3';

    // Se l'allaccio è da riprogrammare
    if (this.egridConnectionStatus === 'ko') return 'red';

    return '';
  }

  get connectionTooltipColor(): string {
    if (isTaskCompleted(this.operationChild, 'R')) return 'success';
    if (isTaskFailed(this.operationChild, 'R')) return 'error';

    if (!this.egridConnectionStatus && this.connectionLastStep) return 'orange';

    // Allacciamento
    switch (this.egridConnectionStatus) {
      case 'suggested':
      case 'not-confirmed':
      case 'no-confirmation-required':
        return 'orange';

      case 'confirmed':
      case 'connected':
        return 'success';

      case 'na':
        return 'grey';

      case 'ko':
      default:
        return 'error';
    }
  }

  get connectionTooltipText() {
    if (isTaskCompleted(this.operationChild, 'R')) return 'Allacciamento completato';

    // Allacciamento
    switch (this.egridConnectionStatus) {
      case 'suggested':
        return 'Date proposte';

      case 'not-confirmed':
        return 'Data concordata (da confermare a portale)';

      case 'no-confirmation-required':
        return 'Data concordata (non necessaria conferma a portale)';

      case 'confirmed':
        return 'Data confermata';

      case 'connected':
        return 'Impianto in esercizio';

      case 'ko':
        return 'Allaccio KO da riprogrammare';

      case 'na':
        return 'Allaccio non applicabile';

      default:
        return 'Data prevista per l\'allacciamento da definire';
    }
  }

  get connectionClass(): string {
    if (this.connectionLastStep) return '';

    return !this.egridConnectionStatus
      ? 'grey--text text--lighten-2'
      : '';
  }

  get connectionText(): string {
    switch (this.egridConnectionStatus) {
      case 'suggested': {
        const suggestedDateTime1 = this.suggestedEgridConnectionTime1
          ? `${this.suggestedEgridConnectionDate1} ${this.suggestedEgridConnectionTime1}`
          : `${this.suggestedEgridConnectionDate1}`;

        let suggestedDateTime2 = '';
        if (this.suggestedEgridConnectionDate2) {
          suggestedDateTime2 = this.suggestedEgridConnectionTime2
            ? `<br>${this.suggestedEgridConnectionDate2} ${this.suggestedEgridConnectionTime2}`
            : `<br>${this.suggestedEgridConnectionDate2}`;
        }

        return `ALLACCIAMENTO<br>${suggestedDateTime1}${suggestedDateTime2}`;
      }

      case 'not-confirmed':
      case 'no-confirmation-required':
      case 'confirmed':
        return this.egridConnectionTime
          ? `ALLACCIAMENTO<br>${this.connectionDate} ${this.egridConnectionTime}`
          : `ALLACCIAMENTO<br>${this.connectionDate}`;

      case 'connected':
        return this.connectionActiveDate
          ? `ALLACCIAMENTO<br>${this.connectionActiveDate}`
          : 'ALLACCIAMENTO';

      case 'na':
        return 'Non applicabile';

      case 'ko':
        return 'ALLACCIAMENTO<br>KO';

      default:
        if (this.connectionLastStep) {
          return this.connectionLastStepDate
            ? `${this.connectionLastStep}<br>${this.connectionLastStepDate}`.toUpperCase()
            : `${this.connectionLastStep}`.toUpperCase();
        }

        return 'da definire';
    }
  }

  // Azioni

  onEdit() {
    console.log(`${this.constructor.name}: onEdit()`); // eslint-disable-line no-console

    this.$emit('edit');
  }
}
